import { Injectable } from '@angular/core';
import {
	CachePolicy,
	CachePolicyEntity,
	UrlService,
} from '@datachain/ui-sdk/common';
import { Observable } from 'rxjs';

import { DcCoreElementType } from '../../core/dc-core-types';
import { ElementPermission } from '../../core/domain/permission-types';
import { DcBaseRequester } from './dc-base.requester';
import {
	BeElement,
	BePermissions,
	IGlobalPermission,
	PermissionResponse,
} from './models/permission.model';

@Injectable({
	providedIn: 'root',
})
export class PermissionRequester extends DcBaseRequester<unknown> {
	protected resource: string;
	protected elementsListUrl: string;
	protected singleElementUrl: string;

	public constructor(private readonly urlService: UrlService) {
		super();
		this.resource = 'unspecified';
		this.elementsListUrl = this.resource;
		this.singleElementUrl = this.resource;
	}

	public getKnownPermissions(): Observable<PermissionResponse> {
		const url = this.urlService.generateUrl(
			this.generateResourceUrl('PERMISSIONS_CODES'),
			{},
			{}
		);

		return this.get<PermissionResponse>(
			url,
			CachePolicyEntity.build({
				isCacheable: false,
				policy: CachePolicy.NetworkOnly,
				maxAgeInSeconds: 0,
			})
		);
	}

	public getCurrentUserGlobalPermissions(): Observable<ReadonlyArray<string>> {
		const url = this.urlService.generateUrl(
			this.generateResourceUrl('PERMISSIONS_CURRENT_USER_GLOBAL'),
			{},
			{
				project: this.projectId,
			}
		);

		return this.get<ReadonlyArray<string>>(
			url,
			CachePolicyEntity.build({
				isCacheable: false,
				policy: CachePolicy.NetworkAndCache,
				maxAgeInSeconds: 0,
			})
		);
	}

	public getCurrentUserElementPermissions(
		elementType: DcCoreElementType,
		elementId: number
	): Observable<Record<ElementPermission, boolean>> {
		const url = this.urlService.generateUrl(
			this.generateResourceUrl('PERMISSIONS_ELEMENT_RIGHTS'),
			{
				type: elementType.toLowerCase(),
				elementId,
			},
			{
				project: this.projectId,
			}
		);

		return this.get<Record<ElementPermission, boolean>>(
			url,
			CachePolicyEntity.build({
				isCacheable: false,
				policy: CachePolicy.NetworkAndCache,
				maxAgeInSeconds: 0,
			})
		);
	}

	public haveCreatePermission(el: DcCoreElementType): Observable<boolean> {
		const url = this.urlService.generateUrl(
			this.generateResourceUrl('PERMISSIONS_CHECK_USER_GLOBAL_PERMISSION'),
			{
				action: `${el.toLowerCase()}-${ElementPermission.Create}`,
			},
			{}
		);
		return this.get<boolean>(
			url,
			CachePolicyEntity.build({
				isCacheable: false,
				policy: CachePolicy.NetworkOnly,
				maxAgeInSeconds: 0,
			})
		);
	}

	// not used!
	public getUserGlobalPermissions(
		userUuid: string
	): Observable<ReadonlyArray<IGlobalPermission>> {
		const url = this.urlService.generateUrl(
			this.generateResourceUrl('PERMISSIONS_BY_USER_GLOBAL'),
			{
				userId: userUuid,
			},
			{
				project: this.projectId,
			}
		);

		return this.get<ReadonlyArray<IGlobalPermission>>(
			url,
			CachePolicyEntity.build({
				isCacheable: false,
				policy: CachePolicy.NetworkOnly,
				maxAgeInSeconds: 0,
			})
		);
	}

	// It is used just in expo
	public haveGlobalPermission(permission: BePermissions): Observable<boolean> {
		const url = this.urlService.generateUrl(
			this.generateResourceUrl('PERMISSIONS_CHECK_USER_GLOBAL_PERMISSION'),
			{
				action: permission,
			},
			{}
		);
		return this.get<boolean>(
			url,
			CachePolicyEntity.build({
				isCacheable: false,
				policy: CachePolicy.NetworkOnly,
				maxAgeInSeconds: 0,
			})
		);
	}

	// It is used just in expo
	protected haveActionPermissionOnElement(
		elementId: number,
		element: BeElement,
		permission: BePermissions
	): Observable<boolean> {
		const url = this.urlService.generateUrl(
			this.generateResourceUrl('PERMISSIONS_CHECK_PERMISSION'),
			{
				elementId,
				type: element,
				action: permission,
			},
			{
				project: this.projectId,
			}
		);
		return this.get<boolean>(
			url,
			CachePolicyEntity.build({
				isCacheable: false,
				policy: CachePolicy.NetworkOnly,
				maxAgeInSeconds: 0,
			})
		);
	}
}
